<template>
    <AuthLayout :imageSrc="currentImage" :layoutType="step === 8 ? 'full' : ''">
        <div class="flex justify-content-center align-items-center w-full">
            <Card class="motivation-card" :style="step === 8 ? 'max-width: 80%' : ''">
                <template #content>
                    <div class="flex flex-column align-items-center text-center">
                        <div class="mb-4" v-if="step !== 1 && onboardingContent.title">
                            <h2 class="Paragraph-Medium mb-2">{{ onboardingContent.title }}</h2>
                            <p class="Caption">{{ onboardingContent.caption }}</p>
                        </div>
                        <RegistrationView   @registration-success="setRegistrationData" v-if="step === 1" />
                        <FirstOnboardingView @motivations-selected="setMotivations"          v-if="step === 2" />
                        <SecondOnboardingView @study-level-selected="setStudyLevelAndSubjects" v-if="step === 3" />
                        <ThirdOnboardingView  @way-of-study-selected="setWayOfStudy"           v-if="step === 4" />
                        <FourthObardingFlow   @budget-selected="setBudget"                     v-if="step === 5" />
                        <FifthObardingFlow    @parents-details-selected="setParentsDetails"    v-if="step === 6" />
                        <SixthObardingFlow    @profile-image-selected="setProfileImage"        v-if="step === 7" />
                        <SeventhObardingFlow  @package-selected="finalizeOnboarding" v-if="step === 8" />
                    </div>
                </template>
            </Card>
        </div>
    </AuthLayout>
</template>

<script setup>
import { ref, defineProps, watch, onMounted, computed } from 'vue';
import { useRouter } from "vue-router";
import Card from 'primevue/card';
import AuthLayout from "@/layout/AuthLayout.vue";
import RegistrationView      from "@/components/RegistrationComponent.vue"
import FirstOnboardingView   from "@/components/studentOnbardingFlow/stdOnboardingFirstPage.vue"
import SecondOnboardingView  from "@/components/studentOnbardingFlow/stdOnboardingSecondPage.vue"
import ThirdOnboardingView   from "@/components/studentOnbardingFlow/stdOnboardingThirdPage.vue"
import FourthObardingFlow    from "@/components/studentOnbardingFlow/stdOnboardingFourthPage.vue"
import FifthObardingFlow     from "@/components/studentOnbardingFlow/stdOnboardingFifthPage.vue"
import SixthObardingFlow     from "@/components/studentOnbardingFlow/stdOnboardingSixthPage.vue"
import SeventhObardingFlow   from "@/components/studentOnbardingFlow/stdOnboardingSeventhPage.vue"
import { updateProfile, getProfile } from "@/api/student";
import { notifySuccess, notifyContrast } from '@/utils/notification';
import { useAuthStore } from "@/stores/authStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import step1Img from "@/assets/Images/studentRegister.png";
import step2Img from "@/assets/Images/stdOnboarding1.png";
import step3Img from "@/assets/Images/stdOnboarding2.png";
import step4Img from "@/assets/Images/stdOnboarding3.png";
import step5Img from "@/assets/Images/stdOnboarding4.png";

const router            = useRouter();
const authStore         = useAuthStore();
const subscriptionStore = useSubscriptionStore();

// Institute-sponsored members already have a plan and must not see the package step.
const managedByInstitute = computed(() => subscriptionStore.managedByInstitute);

const props = defineProps({
    registrationData: { type: Object, required: false }
});

const localData        = ref(props.registrationData);
const step             = ref(1);
const alreadyLoggedIn  = ref(false);

const onboardingContent = computed(() => {
    const username = (authStore.user?.username || "").split(' ')[0];
    const contents = {
        1: { title: "", caption: "" },
        2: { title: `Hey ${username}. Excited to learn new things?`,       caption: "Let's answer a few questions to give you a better learning experience." },
        3: { title: `Hey ${username}. Excited to learn new things?`,       caption: "Let's answer a few questions to give you a better learning experience." },
        4: { title: `Hey ${username}. Excited to learn new things?`,       caption: "Let's answer a few questions to give you a better learning experience." },
        5: { title: `Hey ${username}. Excited to learn new things?`,       caption: "Let's answer a few questions to give you a better learning experience." },
        6: { title: `Hey ${username}. Excited to learn new things?`,       caption: "Let's add details about your parents/guardians details here." },
        7: { title: `Hey ${username}. Excited to learn new things?`,       caption: "Ride the iLearn'd wave uploading your profile pic." },
        8: { title: `Hey ${username}, Let's finish the setup by choosing a package`, caption: "Let's choose a package that best fits your needs. You can upgrade or downgrade anytime." }
    };
    return contents[step.value] || contents[2];
});

// Determine which step to resume based on saved profile data
const getOnboardingStep = (user) => {
    if (!user.learningMotivation?.length)                                       return 2;
    if (!user.currentEducationLevel || !user.interestedSubjects?.length)        return 3;
    if (!user.preferredLearningMode?.length || !user.preferredMedium)           return 4;
    if (!user.dateOfBirth || user.minimumBudget == null)                        return 5;
    if (!user.guardians?.length)                                                return 6;
    if (!user.profilePhotoURL)                                                  return 7;
    return 8;
};

onMounted(async () => {
    if (!authStore.user) return;
    alreadyLoggedIn.value = true;
    try {
        // Decide early whether this is an institute-sponsored member (plan granted at
        // account creation, so the flag is true from the very first login).
        const [response] = await Promise.all([getProfile(), subscriptionStore.fetchMy()]);
        const freshUser = response.data.data;
        authStore.user  = { email: authStore.user.email, ...freshUser };
        localData.value = { ...freshUser };

        const goToStep8Signal = sessionStorage.getItem('studentOnboardingGoToStep8');
        if (goToStep8Signal) sessionStorage.removeItem('studentOnboardingGoToStep8');

        if (props.registrationData) {
            step.value = 2;
        } else if (!managedByInstitute.value &&
            (goToStep8Signal || sessionStorage.getItem('studentOnboardingPendingPackage'))) {
            step.value = 8;
        } else {
            step.value = getOnboardingStep(freshUser);
        }

        // Sponsored members never see the package step — finalize once everything else is done.
        if (managedByInstitute.value && step.value === 8) {
            await finalizeOnboarding();
            return;
        }

        notifyContrast("Complete Your Onboarding", "Hi " + freshUser.username +
            "! It looks like you didn't finish setting up your profile. Please complete your onboarding process.");
    } catch {
        notifyContrast("Error", "Could not fetch your profile. Please try refreshing the page.");
    }
});

watch(() => props.registrationData, (newVal) => { localData.value = newVal; });

const submitData = async (payload) => {
    try {
        await updateProfile(payload);
        authStore.user = { ...authStore.user, ...payload };
    } catch {
        throw new Error("Failed to update profile");
    }
};

// ── Step handlers — save each step immediately so re-login resumes correctly ──

const setRegistrationData = (data) => {
    localData.value       = data;
    step.value            = 2;
    alreadyLoggedIn.value = true;
};

const setMotivations = async (data) => {
    try {
        await submitData({ learningMotivation: data });
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    step.value = 3;
};

const setStudyLevelAndSubjects = async (data) => {
    try {
        await submitData({
            currentInstitution:    data.college,
            currentEducationLevel: data.selectedGrade.code,
            interestedSubjects:    data.selectedSubjects.map(i => i.code),
            selectedExamMedium:    data.selectedExamMedium?.code || null,
        });
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    step.value = 4;
};

const setWayOfStudy = async (data) => {
    try {
        await submitData({
            preferredLearningMode: data.learningMethods.map(i => i.value),
            preferredClassTime:    data.preferredTime.map(i => i.value),
            preferredMedium:       data.preferredMedium.value,
        });
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    step.value = 5;
};

const setBudget = async (data) => {
    try {
        await submitData({
            minimumBudget: data.minValue,
            maximumBudget: data.maxValue,
            dateOfBirth:   data.dateOfBirth,
            nationality:   data.nationality,
        });
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    step.value = 6;
};

const setParentsDetails = async (data) => {
    try {
        await submitData({
            guardians: data.guardians.map(g => ({
                name:          g.name,
                contactNumber: g.contactNumber,
                email:         g.email,
                isPrimary:     g.isPrimary,
            })),
        });
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    step.value = 7;
};

const setProfileImage = async (data) => {
    try {
        await submitData({ profilePhotoURL: data });
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    // Sponsored members skip the package step entirely — finish onboarding here.
    if (managedByInstitute.value) {
        await finalizeOnboarding();
        return;
    }
    step.value = 8;
};

const finalizeOnboarding = async (pkg) => {
    try {
        const payload = { registrationCompleted: true };
        // Sponsored members get their plan from the backend — never set/clear the package here.
        if (!managedByInstitute.value) {
            payload.subscriptionPackage = pkg?.id || authStore.user?.subscriptionPackage || null;
        }
        await submitData(payload);
        notifySuccess("Welcome to iLearn'd!", "Your profile is ready. Let's get started!");
        router.push("/student_dashboard");
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
    }
};

const currentImage = computed(() => {
    switch (step.value) {
        case 1: return step1Img;
        case 2: return step2Img;
        case 3: return step3Img;
        case 4: return step4Img;
        case 5: return step5Img;
        case 6: return step3Img;
        case 7: return step3Img;
        case 8: return null;
        default: return "";
    }
});
</script>

<style scoped>
.motivation-card {
    width: 100%;
    max-width: 700px;
    border: none;
    box-shadow: none;
}
</style>
